import { useState, useEffect, useRef, useCallback } from 'react';
import { initializeVoiceCall, setupVoiceCallHandlers, makeCall } from '../../channels/voice/voiceCallFeature';
import type { MakeCallOptions, VoiceCallConfig } from '../../channels/voice/voiceCallFeature';
import type { UA } from 'jssip';
import SocketHandler from '../../channels/voice/CamlinSocketHandler';
import { showToast } from './uiUtils';

export interface VoiceFeaturesOptions {
  /** Skip SIP/WebRTC initialization (for visual-only mode) */
  noSip?: boolean
  /** Provision per-user WebRTC credentials via CamlinVoice before registering */
  provisionUserId?: string
  /** Base URL for CamlinVoice API calls */
  voiceApiBaseUrl?: string
  /** Disable the legacy nuassist socket bridge */
  disableLegacySocket?: boolean
  /** Optional SIP host override */
  sipHost?: string
  /** Optional SIP WebSocket URL override */
  sipWebSocketUrl?: string
}

interface ProvisionResponse {
  endpoint: string
  password: string
  sipDomain: string
  created?: boolean
}

type ProvisionError = Error & {
  statusCode?: number
  retryable?: boolean
}

const PROVISION_RETRY_DELAYS_MS = [1200, 2500, 4000]

function hasActiveVoiceSession(session: any): boolean {
  if (!session) {
    return false
  }

  try {
    if (typeof session.isEnded === 'function' && session.isEnded()) {
      return false
    }

    if (typeof session.isTerminated === 'function' && session.isTerminated()) {
      return false
    }

    if (typeof session.isInProgress === 'function' && session.isInProgress()) {
      return true
    }

    if (typeof session.isEstablished === 'function' && session.isEstablished()) {
      return true
    }
  } catch (error) {
    console.warn('Unable to inspect current voice session state:', error)
  }

  return false
}

function normalizeVoiceApiBaseUrl(baseUrl?: string): string {
  if (!baseUrl || baseUrl.trim().length === 0) {
    return '/voice-api'
  }

  return baseUrl.replace(/\/$/, '')
}

function createProvisionError(message: string, statusCode?: number, retryable = false): ProvisionError {
  const error = new Error(message) as ProvisionError
  error.statusCode = statusCode
  error.retryable = retryable
  return error
}

function isRetryableProvisionError(error: unknown): error is ProvisionError {
  return Boolean(error && typeof error === 'object' && (error as ProvisionError).retryable === true)
}

function waitFor(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

async function provisionWebRtcEndpoint(userId: string, baseUrl?: string): Promise<ProvisionResponse> {
  const provisionUrl = `${normalizeVoiceApiBaseUrl(baseUrl)}/api/webrtc/provision`

  for (let attempt = 0; attempt <= PROVISION_RETRY_DELAYS_MS.length; attempt += 1) {
    const response = await fetch(provisionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    })

    if (response.ok) {
      return response.json()
    }

    const errorPayload = await response.json().catch(() => ({ error: response.statusText }))
    const message = errorPayload.message || errorPayload.error || `Provisioning failed (${response.status})`
    const retryable = response.status === 503 || response.status >= 500

    if (retryable && attempt < PROVISION_RETRY_DELAYS_MS.length) {
      const delayMs = PROVISION_RETRY_DELAYS_MS[attempt]
      console.warn(`[VoiceFeatures] WebRTC provisioning unavailable (attempt ${attempt + 1}), retrying in ${delayMs}ms`, {
        userId,
        status: response.status,
        message
      })
      await waitFor(delayMs)
      continue
    }

    throw createProvisionError(message, response.status, retryable)
  }

  throw createProvisionError('Provisioning failed', undefined, false)
}

export function useVoiceFeatures(
  projectTokenCamlin: string,
  handoffTokenCamlin: string,
  handleEvent: (eventName: string, data: any, props?: any) => void,
  options?: VoiceFeaturesOptions
) {
  const [ua, setUa] = useState<UA | null>(null);
  const [isVoiceInitialized, setIsVoiceInitialized] = useState(false);
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [isInVoiceCall, setIsInVoiceCall] = useState(false);
  const socketHandlerRef = useRef<SocketHandler | null>(null);

  useEffect(() => {
    // Skip SIP initialization if noSip is true (visual-only mode)
    if (options?.noSip) {
      console.log('Voice features disabled (nosip=true) - skipping SIP initialization');
      setUa(null);
      setIsVoiceInitialized(false);
      return;
    }

    let cleanup = () => {};
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    setIsVoiceInitialized(false);

    const initialize = async () => {
      try {
        const voiceConfig: VoiceCallConfig = {
          host: options?.sipHost,
          webSocketUrl: options?.sipWebSocketUrl,
          onRegistrationChange: (ready) => {
            if (!cancelled) {
              setIsVoiceInitialized(ready);
            }
          }
        };

        if (options?.provisionUserId) {
          const provisioned = await provisionWebRtcEndpoint(options.provisionUserId, options?.voiceApiBaseUrl);
          if (cancelled) {
            return;
          }
          if (typeof window !== 'undefined') {
            const provisionInfo = {
              userId: options.provisionUserId,
              endpoint: provisioned.endpoint,
              sipDomain: provisioned.sipDomain,
              created: provisioned.created
            };
            console.log('[VoiceFeatures] WebRTC endpoint provisioned', provisionInfo);
            (window as any).__CAMLIN_LIVE_WEBRTC_ENDPOINT__ = provisionInfo;
          }
          voiceConfig.username = provisioned.endpoint;
          voiceConfig.password = provisioned.password;
          voiceConfig.host = options?.sipHost || provisioned.sipDomain || voiceConfig.host;
        }

        const userAgent = initializeVoiceCall(voiceConfig);
        if (cancelled) {
          userAgent.stop();
          return;
        }

        if (userAgent && userAgent.ua) {
          setUa(userAgent.ua);
          cleanup = setupVoiceCallHandlers(userAgent, handleEvent);
        }
      } catch (error) {
        console.error('Error initializing voice features:', error);
        setUa(null);
        setIsVoiceInitialized(false);

        if (!cancelled && isRetryableProvisionError(error)) {
          const retryDelayMs = 4000
          console.warn(`[VoiceFeatures] Voice backend not ready, retrying initialization in ${retryDelayMs}ms`)
          retryTimer = window.setTimeout(() => {
            retryTimer = null
            void initialize()
          }, retryDelayMs)
          return
        }

        const message = error instanceof Error ? error.message : 'Failed to initialize voice features'
        showToast(message, 'error');
      }
    };

    void initialize();

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer)
      }
      cleanup();
    };
  }, [
    handleEvent,
    options?.noSip,
    options?.provisionUserId,
    options?.voiceApiBaseUrl,
    options?.sipHost,
    options?.sipWebSocketUrl
  ]);

  const initiateCall = useCallback((target: string, callOptions?: MakeCallOptions) => {
    try {
      if (!isVoiceInitialized || !ua) {
        showToast('Voice not ready. Please try again.', 'warning');
        return false;
      }
      const uaAny = ua as any;
      const isConnected = typeof uaAny.isConnected === 'function' ? uaAny.isConnected() : true;
      const isRegistered = typeof uaAny.isRegistered === 'function' ? uaAny.isRegistered() : true;
      if (!isConnected || !isRegistered) {
        showToast('Voice is still connecting. Please try again in a moment.', 'warning');
        return false;
      }
      if (hasActiveVoiceSession(currentSession)) {
        showToast('A voice call is already connecting or active.', 'warning');
        return false;
      }
      let session: any = null;
      let connectionLossCleanupTriggered = false;

      const syncDebugSessionHandles = () => {
        if (typeof window === 'undefined') {
          return;
        }

        (window as any).__CAMLIN_LIVE_WEBRTC_SESSION__ = session;
        (window as any).__CAMLIN_LIVE_WEBRTC_PC__ = session?.connection ?? null;
      };

      const clearDebugSessionHandles = () => {
        if (typeof window === 'undefined') {
          return;
        }

        const currentDebugSession = (window as any).__CAMLIN_LIVE_WEBRTC_SESSION__;
        if (currentDebugSession === session) {
          (window as any).__CAMLIN_LIVE_WEBRTC_SESSION__ = null;
          (window as any).__CAMLIN_LIVE_WEBRTC_PC__ = null;
        }
      };

      const cleanupFailedSession = () => {
        setCurrentSession((existingSession: any) => existingSession === session ? null : existingSession);
        setIsInVoiceCall(false);
        clearDebugSessionHandles();
      };

      const wrappedCallOptions: MakeCallOptions = {
        ...callOptions,
        onSessionStateChange: (state, detail) => {
          if (state === 'connection-lost') {
            cleanupFailedSession();

            if (!connectionLossCleanupTriggered) {
              connectionLossCleanupTriggered = true;
              try {
                const isEnded = typeof session?.isEnded === 'function' ? session.isEnded() : false;
                const isTerminated = typeof session?.isTerminated === 'function' ? session.isTerminated() : false;
                if (!isEnded && !isTerminated && typeof session?.terminate === 'function') {
                  session.terminate();
                }
              } catch (cleanupError) {
                console.warn('Error terminating failed voice session:', cleanupError);
              }
            }
          }

          if (state === 'failed' || state === 'ended') {
            cleanupFailedSession();
          }

          callOptions?.onSessionStateChange?.(state, detail);
        }
      };

      session = makeCall(ua, target, wrappedCallOptions);
      syncDebugSessionHandles();
      session?.on?.('accepted', () => {
        setCurrentSession(session);
        setIsInVoiceCall(true);
        syncDebugSessionHandles();
      });
      session?.on?.('confirmed', () => {
        setCurrentSession(session);
        setIsInVoiceCall(true);
        syncDebugSessionHandles();
      });
      session?.on?.('ended', () => {
        setCurrentSession((existingSession: any) => existingSession === session ? null : existingSession);
        setIsInVoiceCall(false);
        clearDebugSessionHandles();
      });
      session?.on?.('failed', () => {
        setCurrentSession((existingSession: any) => existingSession === session ? null : existingSession);
        setIsInVoiceCall(false);
        clearDebugSessionHandles();
      });
      setCurrentSession(session);
      setIsInVoiceCall(true);
      console.log('Call initiated');
      return true;
    } catch (error) {
      console.error('Error initiating call:', error);
      showToast('Failed to initiate call', 'error');
      return false;
    }
  }, [currentSession, isVoiceInitialized, ua]);

  // Add socket handler setup
  useEffect(() => {
    if (options?.disableLegacySocket) {
      socketHandlerRef.current = null;
      return;
    }

    if (projectTokenCamlin && handoffTokenCamlin && !socketHandlerRef.current) {
      socketHandlerRef.current = new SocketHandler(projectTokenCamlin, handoffTokenCamlin, 'avatar', handleEvent as any);
    }
    return () => {
      if (socketHandlerRef.current) {
        socketHandlerRef.current = null;
      }
    };
  }, [projectTokenCamlin, handoffTokenCamlin, handleEvent, options?.disableLegacySocket]);

  return {
    ua,
    isVoiceInitialized,
    currentSession,
    isInVoiceCall,
    setIsInVoiceCall,
    initiateCall
  };
}
